import Image from 'next/image'
import Link from 'next/link'
import { getReadableImageUrl } from '@/lib/directus'
import HomeTopForm from '@/components/ContactForm/HomeTopForm'
import Breadcrumbs from '@/components/Secondary/Breadcrumbs/Breadcrumbs'
import { sanitizeHTML } from '@/lib/utils'

interface SecHeroProps {
	heading?: {
		title?: string
	}
	subheading?: {
		title?: string
	}
	text?: {
		text?: string
	}
	button?: {
		href?: string
		label?: string
	}
	image_mobile?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_disk: string
			}
		}
	}
	image_desktop?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_disk: string
			}
		}
	}
	showBreadcrumbs?: boolean
}

export const fields = [
	// headings
	'heading.title',
	'subheading.title',

	// text
	'text.text',

	// button
	'button.href',
	'button.label',

	// background images
	'image_mobile.alt',
	'image_mobile.image_file.image.id',
	'image_mobile.image_file.image.filename_download',

	'image_desktop.alt',
	'image_desktop.image_file.image.id',
	'image_desktop.image_file.image.filename_download',
] as const

export default function SecHero({ data, showBreadcrumbs }: { data: SecHeroProps, showBreadcrumbs?: boolean }) {
  return (
		<>
			<section className='SecHero relative overflow-hidden md:h-[45rem] 2xl:h-[51.25rem] xl:h-[45rem]'>
				{/* Background Image */}
				<div className='relative md:absolute inset-0 w-full h-[15rem] md:h-full'>
					<picture>
						{data?.image_desktop?.image_file?.image?.id && (
							<source
								srcSet={getReadableImageUrl(
									data.image_desktop.image_file.image.id,
									data.image_desktop.image_file.image.filename_disk || ''
								)}
								media='(min-width: 768px)'
								type='image/webp'
							/>
						)}
						{data?.image_mobile?.image_file?.image?.id && (
							<Image
								src={getReadableImageUrl(
									data.image_mobile.image_file.image.id,
									data.image_mobile.image_file.image.filename_disk || ''
								)}
								alt={data.image_desktop?.alt || ''}
								fill
								className='object-cover object-top'
								priority
								quality={70}
								sizes='(max-width: 48rem) 100vw, (max-width: 80rem) 100vw, 100vw'
								fetchPriority='high'
							/>
						)}
					</picture>
					{/* Gradient overlay for mobile - reduced height by half */}
					<div className='absolute inset-x-0 bottom-0 h-[30%] bg-gradient-to-t from-white to-transparent md:hidden' />
				</div>

				{/* Content */}
				<div className='container relative flex h-full items-center'>
					<div className='flex flex-col gap-8 md:gap-12 max-w-[33.75rem] max-md:py-12'>
						{/* Headings */}
						<div className='grid gap-2'>
							<h1
								dangerouslySetInnerHTML={data?.heading?.title ? sanitizeHTML(data.heading.title) : undefined}
							/>
							{data?.subheading?.title && (
								<span className='text-xl md:text-3xl uppercase tracking-wider block mt-2 md:mt-4'>
									{data.subheading.title || ''}
								</span>
							)}
						</div>

						{data?.text?.text && (
							<div
								className='grid gap-6'
								dangerouslySetInnerHTML={sanitizeHTML(data.text.text)}
							/>
						)}

						{data?.button?.href && data?.button?.label && (
							<div className='flex justify-start'>
								<Link
									href={data.button?.href || '/'}
									className='btn'
									onClick={e => {
										if (data.button?.href) {
											e.preventDefault()
											window.location.href = data.button.href
										}
									}}
								>{data.button.label}
								</Link>
							</div>
						)}
					</div>
				</div>
			</section>
			<HomeTopForm />
			<Breadcrumbs />
		</>
	)
}